import { invariant } from '@seen/core/invariant'
import { Order } from '@seen/core/models/orders/Order'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { cn, Tabs, TabsList, TabsTrigger } from '@seen/ui'
import { compact, flatten, zip } from 'lodash-es'
import { type JSX, type ReactNode, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'
import { z } from 'zod'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import { OrderListRow } from '#src/components/order/OrderListRow.tsx'
import { TaskRow } from '#src/components/task/list/TaskRow.tsx'
import { useForm } from '#src/hooks/useForm.ts'
import { useFullTextSearch } from '#src/hooks/useFullTextSearch.ts'

type Tab = 'all' | 'tasks' | 'orders'

export function SearchPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  const searchParam = searchParams.get('q') ?? ''
  const [search, setSearch] = useState(searchParam)

  const tabParam = (searchParams.get('tab') ?? 'all') as Tab
  const [tab, setTab] = useState<Tab>(tabParam)

  const form = useForm({
    schema: formSchema,
    defaultValues: {
      search: searchParam,
    },
  })

  const showTasks = tab === 'all' || tab === 'tasks'
  const { data: tasksBundle, isLoading: isTasksLoading } = useFullTextSearch(
    StandardTask,
    StandardTask.baseSearchQuery(),
    {
      fullTextQuery: search,
      enabled: showTasks && !!search,
      searchType: 'hybrid',
    }
  )
  const tasks = useMemo(
    () =>
      tasksBundle?.entry?.map(({ resource }) => {
        invariant(resource && isOfType(resource, 'Task'), 'Task not found')
        return StandardTask.fromFhirResource(resource)
      }),
    [tasksBundle]
  )

  const showOrders = tab === 'all' || tab === 'orders'
  const { data: ordersBundle, isLoading: isOrdersLoading } = useFullTextSearch(
    Order,
    Order.queryAll(),
    {
      fullTextQuery: search,
      enabled: showOrders && !!search,
      searchType: 'hybrid',
    }
  )
  const orders = useMemo(
    () =>
      ordersBundle?.entry?.map(({ resource }) => {
        invariant(
          resource && isOfType(resource, 'ServiceRequest'),
          'Order not found'
        )
        return Order.fromFhirResource(resource)
      }),
    [ordersBundle]
  )

  const isLoading =
    (showTasks && isTasksLoading) || (showOrders && isOrdersLoading)

  const rows = useMemo(() => {
    const selectedTasks = showTasks ? (tasks ?? []) : []
    const selectedOrders = showOrders ? (orders ?? []) : []
    const items = interleave(selectedOrders, selectedTasks)
    return items.map((item) => {
      if (item instanceof Order) {
        return (
          <OrderListRow
            key={item.reference.reference}
            order={item}
            hideCheckbox
            prefix={
              tab !== 'orders' && (
                <RowPrefix className="min-w-13.5">Order</RowPrefix>
              )
            }
          />
        )
      }
      if (item instanceof StandardTask) {
        return (
          <TaskRow
            key={item.reference.reference}
            task={item}
            hideCheckbox
            prefix={tab !== 'tasks' && <RowPrefix>Task</RowPrefix>}
          />
        )
      }
      throw new Error('Unknown item')
    })
  }, [tab, tasks, showTasks, orders, showOrders])

  const submit = form.handleSubmit((values) => {
    if (!values.search) {
      return
    }
    setSearch(values.search)
    setSearchParams((prev) => {
      prev.set('q', values.search)
      return prev
    })
  })

  return (
    <div className={cn('relative flex flex-col', 'size-full print:size-auto')}>
      <Helmet title="Search" />
      <div className="flex items-center justify-between gap-2 border-b p-4">
        <Form className="flex flex-1 gap-2" onSubmit={submit}>
          <FormField
            control={form.control}
            name="search"
            render={({ field: { onChange, ...rest } }) => (
              <Input
                onChange={(e) => onChange(e.target.value)}
                placeholder="Search…"
                className="max-w-(--container-prose)"
                {...rest}
              />
            )}
          />
          <SubmitButton submitting={isLoading}>Submit</SubmitButton>
        </Form>
        <Tabs
          value={tab}
          onValueChange={(value) => {
            setTab(value as Tab)
            setSearchParams((prev) => {
              prev.set('tab', value)
              return prev
            })
          }}
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="flex min-h-0 flex-1">
        <VirtualizedList
          key={`${search}-${tab}`}
          rows={rows}
          estimateSize={getRowHeight}
          scrollRestorationId={
            search ? `search-page-q-${search}-tab-${tab}` : null
          }
        />
      </div>
      {isLoading && <Loader />}
    </div>
  )
}

function getRowHeight(): number {
  return 44
}

const formSchema = z.object({
  search: z.string().min(1, { message: 'Search required' }),
})

function interleave<A, B>(a: A[], b: B[]): (A | B)[] {
  return compact(flatten(zip(a, b)))
}

function RowPrefix({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex min-w-16 text-foreground-secondary text-sm',
        className
      )}
    >
      {children}
    </div>
  )
}
